// Canonical node type classification — single source of truth.
// Uses Sets for O(1) lookup. Includes both canonical and legacy alias names.

// Container nodes that can hold children via React Flow parentId. Both the
// "Group" sheet and the "Subnet" I/O container are parents — code that
// validates a parentId or sorts parents-before-children MUST treat both as
// containers (treating only `subnet` as a parent silently strips group
// membership on load — see use-strategy-persistence / snippet-processor).
export const CONTAINER_NODE_TYPES = new Set(['group', 'subnet'])
export const isContainerType = (t: unknown): boolean => t === 'group' || t === 'subnet'

export const MATH_NODES = new Set([
  'add',
  'subtract',
  'multiply',
  'divide',
  'modulo',
  'power',
  'squareRoot',
  'absoluteValue',
  'sum',
  'average',
  'max',
  'min',
  'percentage',
  'percentageChange',
  'customPositionInRange',
  'ratio',
  'slope',
  // Legacy hyphenated aliases
  'square-root',
  'absolute-value',
  'percentage-change',
])

export const DATA_NODES = new Set(['priceData', 'marketData', 'volumeData'])

export const TECHNICAL_NODES = new Set([
  // Canonical names (used in schema registration)
  'simplemovingaverage',
  'exponentialmovingaverage',
  'relativestrengthindex',
  'macd',
  'bollingerbands',
  'weightedmovingaverage',
  'mfi',
  'adi',
  'obv',
  'vwap',
  'averagetruerange',
  'averagedirectionalmovementindex',
  'parabolicsar',
  'keltner-channel',
  'donchian-channel',
  'aroonindicator',
  'knowsurething',
  'williamsr',
  'stochasticfull',
  'stochastic-rsi',
  'rateofchange',
  'trix',
  'awesomeoscillator',
  // Legacy short names
  'sma',
  'ema',
  'rsi',
  'wma',
  'atr',
  'adx',
  'aroon',
  'kst',
  'roc',
  'bollinger-bands',
  'williams-r',
  'stochastic-full',
  'stochastic-oscillator',
  'parabolic-sar',
  'awesome-oscillator',
  // Alternate compound names
  'moneyflowindex',
  'accumulationdistributionindex',
  'onbalancevolume',
  'volumeweightedaverageprice',
  'keltnerChannel',
  'donchianchannel',
  'stochasticoscillator',
  'stochasticrsi',
])

export const LOGIC_NODES = new Set([
  'condition',
  'crossover',
  'merge',
  'switcher',
  'timeout',
  'valueHold',
  'timeSignal',
  'barsConditionCounter',
  'barOffset',
  'cooldown',
])

export const FILTER_NODES = new Set([
  'priceChange',
  'positionInRange',
  'volumeFilter',
  'timeSignal',
  'dailyPercentChange',
  'sessionPercentChange',
  'weeklyPercentChange',
  'monthlyPercentChange',
  'yearlyPercentChange',
  'customPeriodPercentChange',
  'totalVolume',
  'sessionVolume',
  'customPeriodVolume',
  'percentChangeFromOpen',
  'percentChangeFromClose',
  'percentChangeFromHigh',
  'percentChangeFromLow',
])

export const UTILITY_NODES = new Set([
  'barOffset',
  'barSize',
  'barsConditionCounter',
  'rollingMaximum',
  'rollingMinimum',
  'rollingAverage',
  'rollingSum',
  'rollingRange',
  'rollingMidpoint',
  'timePoint',
  'timeSlice',
  'wickSize',
  'signalCounter',
])

export const STRATEGY_NODES = new Set([
  'entryRules',
  'positionSizing',
  'target',
  'stop',
  'multiTarget',
  'trailingStop',
  'trade',
  'simpleTrade',
  'tradeManager',
  'multi-stop',
  'advanced-trade',
])

export const DEBUG_NODES = new Set(['debugValues', 'debugChart'])

/** Dynamic input nodes that need inputs rebuilt from edges */
export const DYNAMIC_INPUT_NODES = new Set(['sum', 'average', 'max', 'min'])

/** Union of all node types that use inputs/outputs data structure */
export const NODES_WITH_INPUTS_OUTPUTS = new Set([
  ...MATH_NODES,
  ...DATA_NODES,
  ...TECHNICAL_NODES,
  ...LOGIC_NODES,
  ...FILTER_NODES,
  ...UTILITY_NODES,
  ...STRATEGY_NODES,
  ...DEBUG_NODES,
])

/** Check if a node type uses inputs/outputs structure */
export function hasInputsOutputs(nodeType: string | undefined): boolean {
  return !!nodeType && NODES_WITH_INPUTS_OUTPUTS.has(nodeType)
}
